import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.startX = 0
    this.currentX = 0
    this.isDragging = false
    this.isPaused = false
    this.remainingTime = 5500
    this.startTime = Date.now()

    // Start auto-close timer
    this.startTimer()

    // Add touch events for swipe-to-dismiss
    this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true })
    this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false })
    this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true })

    // Add mouse events for desktop drag-to-dismiss
    this.element.addEventListener('mousedown', this.handleMouseDown.bind(this))

    // Pause timer on hover
    this.element.addEventListener('mouseenter', this.pauseTimer.bind(this))
    this.element.addEventListener('mouseleave', this.resumeTimer.bind(this))
  }

  startTimer() {
    this.startTime = Date.now()
    this.timeout = setTimeout(() => {
      this.close()
    }, this.remainingTime)
  }

  pauseTimer() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.remainingTime = this.remainingTime - (Date.now() - this.startTime)
      // Minimum 1 second remaining when resuming
      this.remainingTime = Math.max(this.remainingTime, 1000)
      this.isPaused = true
      // Pause the CSS animation
      this.element.style.animationPlayState = 'paused'
    }
  }

  resumeTimer() {
    if (this.isPaused) {
      this.isPaused = false
      // Resume the CSS animation
      this.element.style.animationPlayState = 'running'
      this.startTimer()
    }
  }

  handleTouchStart(e) {
    this.startX = e.touches[0].clientX
    this.currentX = this.startX
    this.isDragging = true

    // Cancel CSS animation so we can control transform directly
    this.element.style.animation = 'none'
    this.element.style.transition = 'none'
    this.element.style.transform = 'translateX(0)'
    this.element.style.opacity = '1'

    this.pauseTimer()
  }

  handleTouchMove(e) {
    if (!this.isDragging) return

    this.currentX = e.touches[0].clientX
    const deltaX = this.currentX - this.startX

    // Only allow swiping right or small resistance left
    const translateX = Math.max(-20, deltaX)

    // Move notification with finger
    this.element.style.transform = `translateX(${translateX}px)`

    // Fade as user swipes right
    if (deltaX > 0) {
      const opacity = Math.max(0.3, 1 - (deltaX / 250))
      this.element.style.opacity = opacity
    }

    // Prevent page scroll while swiping horizontally
    if (Math.abs(deltaX) > 10) {
      e.preventDefault()
    }
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return
    this.isDragging = false

    const deltaX = this.currentX - this.startX

    // If swiped more than 80px right, dismiss
    if (deltaX > 80) {
      this.swipeOut('right')
    } else {
      // Snap back to original position
      this.element.style.transition = 'transform 0.25s ease-out, opacity 0.25s ease-out'
      this.element.style.transform = 'translateX(0)'
      this.element.style.opacity = '1'
      this.resumeTimer()
    }
  }

  handleMouseDown(e) {
    // Ignore if clicking on the close button
    if (e.target.closest('.close-button')) return

    this.startX = e.clientX
    this.isDragging = true
    this.element.style.transition = 'none'
    this.pauseTimer()

    // Add document-level mouse events
    document.addEventListener('mousemove', this.handleMouseMove.bind(this))
    document.addEventListener('mouseup', this.handleMouseUp.bind(this))
  }

  handleMouseMove(e) {
    if (!this.isDragging) return

    this.currentX = e.clientX
    const deltaX = this.currentX - this.startX

    // Only allow dragging right or a small amount left
    const translateX = Math.max(-30, deltaX)

    this.element.style.transform = `translateX(${translateX}px)`

    if (deltaX > 0) {
      const opacity = Math.max(0, 1 - (deltaX / 200))
      this.element.style.opacity = opacity
    }
  }

  handleMouseUp(e) {
    if (!this.isDragging) return
    this.isDragging = false

    // Remove document-level mouse events
    document.removeEventListener('mousemove', this.handleMouseMove.bind(this))
    document.removeEventListener('mouseup', this.handleMouseUp.bind(this))

    const deltaX = this.currentX - this.startX

    if (deltaX > 100) {
      this.swipeOut('right')
    } else {
      this.element.style.transition = 'transform 0.3s ease, opacity 0.3s ease'
      this.element.style.transform = 'translateX(0)'
      this.element.style.opacity = '1'
      this.resumeTimer()
    }
  }

  swipeOut(direction) {
    // Smooth slide out in swipe direction
    this.element.style.transition = 'transform 0.25s ease-out, opacity 0.2s ease-out'
    this.element.style.transform = 'translateX(calc(100% + 3rem))'
    this.element.style.opacity = '0'

    setTimeout(() => {
      this.element.style.display = 'none'
    }, 300)
  }

  close() {
    // Clear any inline styles
    this.element.style.transition = ''
    this.element.style.transform = ''
    this.element.style.opacity = ''

    // Add CSS animation class
    this.element.classList.add('notification-close')

    setTimeout(() => {
      this.element.style.display = 'none'
    }, 400)
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    // Clean up event listeners
    document.removeEventListener('mousemove', this.handleMouseMove.bind(this))
    document.removeEventListener('mouseup', this.handleMouseUp.bind(this))
  }
}
